import { useEffect, useRef, useState } from 'react';
import api from '../api/client.js';

/**
 * Selector de codigo CAByS (13 digitos).
 * Busca en el catalogo local del servidor y permite codigo manual.
 */
export default function CabysPicker({
  value = '',
  onChange,
  onSelectMeta,
  disabled = false,
  label = 'Codigo CAByS',
  required = false,
}) {
  const [q, setQ] = useState(value || '');
  const [abierto, setAbierto] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [meta, setMeta] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [errorFmt, setErrorFmt] = useState('');
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    setQ(value || '');
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const buscar = (texto) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setCargando(true);
      try {
        const { data } = await api.get('/cabys', { params: { q: texto, limite: 25 } });
        setResultados(data.data?.resultados || []);
      } catch {
        setResultados([]);
      } finally {
        setCargando(false);
      }
    }, 220);
  };

  const emitir = (codigo, item) => {
    const limpio = String(codigo || '').replace(/\D/g, '').slice(0, 13);
    setQ(limpio);
    onChange?.(limpio);
    if (item) {
      setMeta(item);
      onSelectMeta?.(item);
    } else if (limpio.length === 13) {
      api.get(`/cabys/${limpio}`)
        .then((r) => {
          setMeta(r.data.data);
          onSelectMeta?.(r.data.data);
          setErrorFmt('');
        })
        .catch((err) => {
          setMeta(null);
          setErrorFmt(err.response?.data?.message || 'Codigo CAByS invalido');
        });
    } else {
      setMeta(null);
      setErrorFmt(limpio ? `Faltan ${13 - limpio.length} digito(s)` : '');
    }
  };

  const onInput = (e) => {
    const raw = e.target.value;
    // Permite pegar codigo o escribir texto de busqueda
    const soloDigitos = /^\d*$/.test(raw.replace(/\s/g, ''));
    if (soloDigitos || raw.replace(/\D/g, '').length >= 10) {
      const dig = raw.replace(/\D/g, '').slice(0, 13);
      setQ(dig);
      onChange?.(dig);
      setAbierto(true);
      buscar(dig);
      if (dig.length === 13) emitir(dig);
      else {
        setMeta(null);
        setErrorFmt(dig ? `Faltan ${13 - dig.length} digito(s)` : '');
      }
    } else {
      setQ(raw);
      setAbierto(true);
      buscar(raw);
    }
  };

  const ok = /^\d{13}$/.test(String(value || q).replace(/\D/g, '')) &&
    String(value || q).replace(/\D/g, '') !== '0000000000000';

  return (
    <div className="field" ref={boxRef} style={{ position: 'relative', gridColumn: '1 / -1' }}>
      <label>
        {label}{required ? ' *' : ''}
        {ok && <span className="badge badge-success" style={{ marginLeft: 8 }}>13 digitos OK</span>}
        {!ok && (value || q) && (
          <span className="badge" style={{ marginLeft: 8, background: 'var(--warning-bg)', color: 'var(--warning)' }}>
            Incompleto
          </span>
        )}
      </label>
      <input
        className="input mono"
        value={q}
        disabled={disabled}
        placeholder="Buscar por nombre o pegar 13 digitos…"
        onChange={onInput}
        onFocus={() => { setAbierto(true); if (!resultados.length) buscar(q); }}
        autoComplete="off"
        maxLength={80}
        style={{
          borderColor: errorFmt && q ? 'var(--danger)' : undefined,
          letterSpacing: /^\d+$/.test(q) ? '0.04em' : undefined,
        }}
      />
      {meta?.descripcion && (
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          {meta.descripcion}
          {meta.tarifa_iva != null && ` · IVA sugerido ${meta.tarifa_iva}%`}
          {meta.grupo && ` · ${meta.grupo}`}
        </div>
      )}
      {errorFmt && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{errorFmt}</div>}
      {!meta?.descripcion && ok && (
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Codigo valido (no esta en el catalogo local frecuente; verifique en BCCR si es el correcto).
        </div>
      )}

      {abierto && (
        <div
          style={{
            position: 'absolute',
            zIndex: 40,
            left: 0,
            right: 0,
            top: '100%',
            marginTop: 4,
            maxHeight: 260,
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,.12)',
          }}
        >
          {cargando && <div className="muted" style={{ padding: 12, fontSize: 13 }}>Buscando…</div>}
          {!cargando && resultados.length === 0 && (
            <div className="muted" style={{ padding: 12, fontSize: 13 }}>
              Sin coincidencias en el catalogo frecuente. Puede escribir los 13 digitos del BCCR.
            </div>
          )}
          {resultados.map((r) => (
            <button
              key={r.codigo}
              type="button"
              onClick={() => {
                emitir(r.codigo, r);
                setAbierto(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '10px 12px',
                border: 0,
                borderBottom: '1px solid var(--border)',
                background: 'transparent',
                cursor: 'pointer',
                color: 'inherit',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--accent-soft)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div className="mono" style={{ fontWeight: 700, fontSize: 13 }}>{r.codigo}</div>
              <div style={{ fontSize: 13 }}>{r.descripcion}</div>
              <div className="muted" style={{ fontSize: 11 }}>
                {r.grupo} · IVA {r.tarifa_iva}%
              </div>
            </button>
          ))}
          <div className="muted" style={{ padding: '8px 12px', fontSize: 11 }}>
            Catalogo local frecuente · Fuente oficial BCCR
          </div>
        </div>
      )}
    </div>
  );
}
